import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useStickyState } from "../safety/listPrefs";
import ResetFiltersChip from "../safety/ResetFiltersChip";
import SafetyShell from "../safety/SafetyShell";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Factory,
  Package,
  Upload,
  Search,
  MoreVertical,
  Trash2,
  Eye,
  QrCode,
  FileSpreadsheet,
  ArrowRightLeft,
  LogOut,
  ArrowLeft,
  Download,
  AlertTriangle,
  FileUp,
  CheckCircle2,
  XCircle,
  FileWarning,
  ClipboardList,
  Plus,
  File,
  Users,
  BarChart3,
  Pencil,
  GitCompareArrows,
  ChevronDown,
  ChevronRight,
  Loader2,
  Flag,
  MessageSquare,
  Send,
  RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

function POManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [permissions, setPermissions] = useState([]);

  // Filters
  const [searchQuery, setSearchQuery] = useStickyState("safety:po:searchQuery", "");
  const [statusFilter, setStatusFilter] = useStickyState("safety:po:statusFilter", "all");
  const [vendorFilter, setVendorFilter] = useStickyState("safety:po:vendorFilter", "all");
  const [productCodeFilter, setProductCodeFilter] = useStickyState("safety:po:productCodeFilter", "");

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, po: null });
  const [deleteQRCodes, setDeleteQRCodes] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // View PO dialog
  const [viewDialog, setViewDialog] = useState({ open: false, po: null, progress: null, progressLoading: false });

  // Replace PO dialog
  const [replaceDialog, setReplaceDialog] = useState({
    open: false, po: null, file: null, previewing: false, preview: null,
    reason: "", allowNumberChange: false, submitting: false,
  });
  const replaceFileInputRef = useRef(null);
  const replaceTargetRef = useRef(null);

  // Duplicate-PO re-upload choice dialog (replace lines vs delete & re-add)
  const [dupDialog, setDupDialog] = useState({ open: false, po_number: "", existing_po_id: "", file: null, busy: false, deleteQR: false });

  // Bulk reupload dialog
  const [bulkDialog, setBulkDialog] = useState({ open: false });
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [missingFileCount, setMissingFileCount] = useState(0);
  const bulkFileInputRef = useRef(null);

  // Preview dialog
  const [previewDialog, setPreviewDialog] = useState({ open: false, po: null, pdfUrl: null, loading: false });

  // Customer assign dialog (shown after upload / for POs missing customer)
  const [customerDialog, setCustomerDialog] = useState({ open: false, poId: null, poNumber: null, mode: "upload" });
  const [customersList, setCustomersList] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [missingCustomerPOs, setMissingCustomerPOs] = useState([]);
  const [missingCustomerDialog, setMissingCustomerDialog] = useState(false);

  // BOM management dialog
  const [bomDialog, setBomDialog] = useState({ open: false, po: null });
  const [boms, setBoms] = useState([]);
  const [bomsLoading, setBomsLoading] = useState(false);
  const [bomUploading, setBomUploading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [bomLabel, setBomLabel] = useState("");
  const [bomNotes, setBomNotes] = useState("");
  const bomFileInputRef = useRef(null);

  // BOM Preview dialog
  const [bomPreviewDialog, setBomPreviewDialog] = useState({ open: false, bom: null, pdfUrl: null });
  const [compareDialog, setCompareDialog] = useState({ open: false, bom: null });
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState("");
  const [reviewDialog, setReviewDialog] = useState({ open: false, po: null });
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewOnlyFlagged, setReviewOnlyFlagged] = useStickyState("safety:po:reviewOnlyFlagged", false);
  const [amendedOnly, setAmendedOnly] = useStickyState("safety:po:amendedOnly", false);
  const [flaggedCount, setFlaggedCount] = useState(0);
  // Sorting
  const [sortColumn, setSortColumn] = useStickyState("safety:po:sortColumn", "order_date");
  const [sortDirection, setSortDirection] = useStickyState("safety:po:sortDirection", "desc");

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        // Reset to default
        setSortColumn("order_date");
        setSortDirection("desc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedPos = [...pos].sort((a, b) => {
    const dir = sortDirection === "asc" ? 1 : -1;
    const valA = a[sortColumn];
    const valB = b[sortColumn];

    if (valA == null && valB == null) return 0;
    if (valA == null) return 1;
    if (valB == null) return -1;

    if (sortColumn === "product_count" || sortColumn === "total_weight_kg") {
      return ((valA || 0) - (valB || 0)) * dir;
    }

    return String(valA).localeCompare(String(valB)) * dir;
  });

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("amended") === "1") {
      setAmendedOnly(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [searchQuery, statusFilter, vendorFilter, productCodeFilter, amendedOnly]);

  const fetchData = async () => {
    try {
      // Fetch permissions
      const permRes = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
      setPermissions(permRes.data.effective_permissions || []);

      // Fetch vendors
      const vendorsRes = await axios.get(`${API}/po/vendors`, getAuthHeaders());
      setVendors(vendorsRes.data);

      // Fetch POs with filters
      let url = `${API}/po/list?`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;
      if (statusFilter !== "all") url += `status_filter=${statusFilter}&`;
      if (vendorFilter !== "all") url += `vendor=${encodeURIComponent(vendorFilter)}&`;
      if (productCodeFilter.trim()) url += `product_code=${encodeURIComponent(productCodeFilter.trim())}&`;
      if (amendedOnly) url += `amended=amended&`;

      const posRes = await axios.get(url, getAuthHeaders());
      setPos(posRes.data);

      // Fetch missing file count
      try {
        const missingRes = await axios.get(`${API}/po/missing-files-count`, getAuthHeaders());
        setMissingFileCount(missingRes.data.missing || 0);
      } catch (_) { /* ignore */ }

      // Fetch flagged-for-review count
      try {
        const flaggedRes = await axios.get(`${API}/po/flagged-count`, getAuthHeaders());
        setFlaggedCount(flaggedRes.data.flagged || 0);
      } catch (_) { /* ignore */ }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load PO data");
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (perm) => permissions.includes(perm);
  const canUpload = hasPermission("po.upload");
  const canDelete = hasPermission("po.delete");
  const canDownload = hasPermission("po.download");
  const canGenerateQR = hasPermission("qr.generate");
  const canBomView = hasPermission("bom.view");
  const canBomUpload = hasPermission("bom.upload");
  const canBomDownload = hasPermission("bom.download");
  const canBomDelete = hasPermission("bom.delete");
  const canEditLineItem = hasPermission("po.line_item.edit");
  const canReplace = hasPermission("po.replace");

  // Edit Line Item dialog state
  const [editLineDialog, setEditLineDialog] = useState({
    open: false,
    po: null,
    line: null,           // current product line
    impact: null,         // GET impact response
    impactLoading: false,
    submitting: false,
    form: { new_product_code: "", new_description: "", new_qty: "", reason: "" },
  });

  // Add Line Item dialog state
  const [addLineDialog, setAddLineDialog] = useState({
    open: false,
    po: null,
    submitting: false,
    form: { product_code: "", description: "", qty: "", rate: "" },
  });

  // Re-parse all stored PO files to backfill missing USD unit prices (fixes $0 sale price)
  const handleBackfillPrices = async () => {
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/po/backfill-prices`, {}, getAuthHeaders());
      const d = res.data || {};
      toast.success(`Prices backfilled · ${d.pos_updated || 0} PO(s), ${d.lines_priced || 0} line(s) priced`, { duration: 7000 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill prices failed");
    } finally {
      setBackfilling(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.xls', '.xlsx', '.xlsm'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!validExtensions.includes(ext)) {
      toast.error("Please upload an Excel file (.xls or .xlsx)");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/po/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message);
      // Open customer assignment dialog for the newly uploaded PO
      const uploaded = response.data;
      await loadCustomersList();
      setSelectedCustomerId("");
      setNewCustomerName("");
      setCustomerDialog({ open: true, poId: uploaded.id, poNumber: uploaded.po_number, mode: "upload" });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Auto-detect an amended re-upload: backend returns 409 {code:"po_exists", existing_po_id}
      if (error.response?.status === 409 && detail && typeof detail === "object" && detail.code === "po_exists") {
        if (canReplace) {
          // Offer a clear choice: replace lines (keep plans/reports) vs delete & re-add.
          setDupDialog({ open: true, po_number: detail.po_number, existing_po_id: detail.existing_po_id, file, busy: false, deleteQR: false });
        } else {
          toast.error(typeof detail === "object" ? detail.message : detail);
        }
      } else {
        toast.error(typeof detail === "object" ? detail.message : (detail || "Failed to upload PO"));
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // View PO details
  const handleViewPO = async (po) => {
    try {
      const response = await axios.get(`${API}/po/${po.id}`, getAuthHeaders());
      setViewDialog({ open: true, po: response.data, progress: null, progressLoading: true });
      // Fetch production progress in the background
      axios.get(`${API}/po/${po.id}/production-progress`, getAuthHeaders())
        .then((pr) => setViewDialog((prev) => prev.open ? { ...prev, progress: pr.data, progressLoading: false } : prev))
        .catch(() => setViewDialog((prev) => prev.open ? { ...prev, progress: null, progressLoading: false } : prev));
    } catch (error) {
      toast.error("Failed to load PO details");
    }
  };

  // Customer assignment helpers
  const loadCustomersList = async () => {
    try {
      const res = await axios.get(`${API}/customers`, getAuthHeaders());
      setCustomersList(res.data);
    } catch (_) { /* ignore */ }
  };

  const openAssignCustomerForPO = async (po) => {
    await loadCustomersList();
    setSelectedCustomerId(po.customer_id || "");
    setNewCustomerName("");
    setCustomerDialog({ open: true, poId: po.id, poNumber: po.po_number, mode: "edit" });
  };

  const saveCustomerAssignment = async () => {
    if (!customerDialog.poId) return;
    let customerId = selectedCustomerId;
    setSavingCustomer(true);
    try {
      if (!customerId && newCustomerName.trim()) {
        const c = await axios.post(`${API}/customers`, { name: newCustomerName.trim() }, getAuthHeaders());
        customerId = c.data.id;
      }
      if (!customerId) {
        toast.error("Select a customer or create a new one");
        return;
      }
      await axios.post(`${API}/po/${customerDialog.poId}/assign-customer`, { customer_id: customerId }, getAuthHeaders());
      toast.success("Customer assigned");
      setCustomerDialog({ open: false, poId: null, poNumber: null, mode: "upload" });
      fetchData();
      // If missing-customer dialog is open, reload missing list
      if (missingCustomerDialog) openMissingCustomerDialog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign customer");
    } finally {
      setSavingCustomer(false);
    }
  };

  const openMissingCustomerDialog = async () => {
    try {
      const [mRes] = await Promise.all([
        axios.get(`${API}/po/missing-customer`, getAuthHeaders()),
        loadCustomersList(),
      ]);
      setMissingCustomerPOs(mRes.data);
      setMissingCustomerDialog(true);
    } catch (_) { toast.error("Failed to load"); }
  };

  // Delete PO
  const handleDeletePO = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }

    setDeleting(true);

    try {
      const url = `${API}/po/${deleteDialog.po.id}?delete_qr_codes=${deleteQRCodes}`;
      const response = await axios.delete(url, getAuthHeaders());

      toast.success(response.data.message);
      setDeleteDialog({ open: false, po: null });
      setDeleteConfirmText("");
      setDeleteQRCodes(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete PO");
    } finally {
      setDeleting(false);
    }
  };

  // Navigate to QR generation with selected PO
  const handleGenerateQR = (po) => {
    navigate(`/safety/qr?po_id=${po.id}`);
  };

  // Download BOQ
  const handleDownloadBOQ = async (po) => {
    try {
      const response = await axios.get(`${API}/po/${po.id}/boq/download`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOQ_${po.po_number.padStart(7, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("BOQ downloaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download BOQ");
    }
  };

  // Download original PO file
  const handleDownloadPOFile = async (po) => {
    try {
      const response = await axios.get(`${API}/po/${po.id}/file/download`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });

      // Extract filename from Content-Disposition header (most reliable for correct extension)
      let downloadName = po.file_name || `PO_${po.po_number}.xlsx`;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) downloadName = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PO file downloaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download PO file");
    }
  };

  // Preview PO file
  const handlePreviewPO = async (po) => {
    if (!canDownload || !po.has_po_file) {
      handleViewPO(po);
      return;
    }
    if (!po.has_preview_pdf) {
      toast.info("PDF preview not yet generated for this PO");
      handleViewPO(po);
      return;
    }
    const token = localStorage.getItem("token");
    const pdfUrl = `${API}/po/${po.id}/file/preview?token=${encodeURIComponent(token)}`;
    setPreviewDialog({ open: true, po, pdfUrl, loading: false });
  };

  const closePreviewDialog = () => {
    setPreviewDialog({ open: false, po: null, pdfUrl: null, loading: false });
  };

  // Bulk reupload handlers
  const handleBulkFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    setBulkFiles(selected);
    setBulkResults(null);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setBulkUploading(true);
    setBulkResults(null);

    try {
      const formData = new FormData();
      bulkFiles.forEach((file) => formData.append('files', file));

      const response = await axios.post(`${API}/po/file/bulk-reupload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      setBulkResults(response.data);
      if (response.data.summary.matched > 0) {
        toast.success(`${response.data.summary.matched} PO file(s) matched and uploaded`);
        fetchData();
      }
      if (response.data.summary.unmatched > 0 || response.data.summary.errors > 0) {
        toast.warning("Some files could not be matched. See details below.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkUploading(false);
    }
  };

  const closeBulkDialog = () => {
    setBulkDialog({ open: false });
    setBulkFiles([]);
    setBulkResults(null);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
  };


  // ====== Edit Line Item Handlers ======
  const openEditLineDialog = async (po, line) => {
    setEditLineDialog({
      open: true,
      po,
      line,
      impact: null,
      impactLoading: true,
      submitting: false,
      form: {
        new_product_code: line.product_code || "",
        new_description: line.description || "",
        new_qty: String(line.qty ?? ""),
        reason: "",
      },
    });
    try {
      const res = await axios.get(
        `${API}/po/${po.id}/products/${encodeURIComponent(line.product_code)}/impact`,
        getAuthHeaders(),
      );
      setEditLineDialog((d) => ({ ...d, impact: res.data?.impact || null, impactLoading: false }));
    } catch (err) {
      setEditLineDialog((d) => ({ ...d, impactLoading: false }));
      toast.error(err.response?.data?.detail || "Failed to load impact preview");
    }
  };

  const closeEditLineDialog = () => {
    setEditLineDialog({
      open: false,
      po: null,
      line: null,
      impact: null,
      impactLoading: false,
      submitting: false,
      form: { new_product_code: "", new_description: "", new_qty: "", reason: "" },
    });
  };

  const submitEditLineItem = async () => {
    const { po, line, form } = editLineDialog;
    if (!po || !line) return;
    if (!form.new_product_code.trim()) {
      toast.error("Product code cannot be empty");
      return;
    }
    if (!form.reason.trim() || form.reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    setEditLineDialog((d) => ({ ...d, submitting: true }));
    try {
      const payload = {
        new_product_code: form.new_product_code.trim(),
        new_description: form.new_description,
        reason: form.reason.trim(),
      };
      if (form.new_qty !== "" && form.new_qty !== null) payload.new_qty = Number(form.new_qty);
      const res = await axios.put(
        `${API}/po/${po.id}/products/${encodeURIComponent(line.product_code)}`,
        payload,
        getAuthHeaders(),
      );
      const cascaded = res.data?.cascaded_count || 0;
      toast.success(
        cascaded > 0
          ? `Line item corrected. ${cascaded} production report${cascaded === 1 ? "" : "s"} auto-updated.`
          : "Line item corrected.",
      );
      closeEditLineDialog();
      await fetchData();
      if (viewDialog.open && viewDialog.po?.id === po.id) {
        try {
          const refreshed = await axios.get(`${API}/po/${po.id}`, getAuthHeaders());
          setViewDialog((v) => ({ ...v, po: refreshed.data }));
        } catch (_) { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update line item");
      setEditLineDialog((d) => ({ ...d, submitting: false }));
    }
  };

  // ====== Add Line Item Handlers ======
  const openAddLineDialog = (po) => {
    setAddLineDialog({
      open: true,
      po,
      submitting: false,
      form: { product_code: "", description: "", qty: "", rate: "" },
    });
  };

  const closeAddLineDialog = () => {
    setAddLineDialog({
      open: false,
      po: null,
      submitting: false,
      form: { product_code: "", description: "", qty: "", rate: "" },
    });
  };

  const submitAddLineItem = async () => {
    const { po, form } = addLineDialog;
    if (!po) return;
    if (!form.product_code.trim()) {
      toast.error("Product code cannot be empty");
      return;
    }
    if (form.qty === "" || form.qty === null) {
      toast.error("Quantity is required");
      return;
    }
    setAddLineDialog((d) => ({ ...d, submitting: true }));
    try {
      const payload = {
        product_code: form.product_code.trim(),
        description: form.description,
        qty: Number(form.qty),
      };
      if (form.rate !== "" && form.rate !== null) payload.rate = Number(form.rate);
      await axios.post(`${API}/po/${po.id}/products`, payload, getAuthHeaders());
      toast.success("Line item added.");
      closeAddLineDialog();
      await fetchData();
      if (viewDialog.open && viewDialog.po?.id === po.id) {
        try {
          const refreshed = await axios.get(`${API}/po/${po.id}`, getAuthHeaders());
          setViewDialog((v) => ({ ...v, po: refreshed.data }));
        } catch (_) { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add line item");
      setAddLineDialog((d) => ({ ...d, submitting: false }));
    }
  };

  const deleteLineItem = async (po, line) => {
    if (!po || !line) return;
    if (!window.confirm(`Remove line item "${line.product_code}" from PO #${po.po_number}?`)) return;
    try {
      await axios.delete(
        `${API}/po/${po.id}/products/${encodeURIComponent(line.product_code)}`,
        getAuthHeaders(),
      );
      toast.success("Line item removed.");
      await fetchData();
      if (viewDialog.open && viewDialog.po?.id === po.id) {
        try {
          const refreshed = await axios.get(`${API}/po/${po.id}`, getAuthHeaders());
          setViewDialog((v) => ({ ...v, po: refreshed.data }));
        } catch (_) { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove line item");
    }
  };


  // ====== Replace PO Handlers ======
  const startReplacePO = (po) => {
    replaceTargetRef.current = po;
    if (replaceFileInputRef.current) replaceFileInputRef.current.value = "";
    replaceFileInputRef.current?.click();
  };

  const onReplaceFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const po = replaceTargetRef.current;
    if (!file || !po) return;
    await openReplaceWithFile(po, file);
  };

  const openReplaceWithFile = async (po, file) => {
    setReplaceDialog({
      open: true, po, file, previewing: true, preview: null,
      reason: "", allowNumberChange: false, submitting: false,
    });
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/po/${po.id}/replace/preview`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "multipart/form-data" },
      });
      setReplaceDialog((d) => ({ ...d, previewing: false, preview: res.data }));
    } catch (err) {
      setReplaceDialog((d) => ({ ...d, previewing: false }));
      toast.error(err.response?.data?.detail || "Failed to read amended PO file");
    }
  };

  // Duplicate-PO choice handlers ---------------------------------------------
  const dupChooseReplace = async () => {
    const { existing_po_id, po_number, file } = dupDialog;
    setDupDialog((d) => ({ ...d, open: false }));
    await openReplaceWithFile({ id: existing_po_id, po_number }, file);
  };

  const dupChooseDeleteReadd = async () => {
    const { existing_po_id, file, deleteQR } = dupDialog;
    setDupDialog((d) => ({ ...d, busy: true }));
    try {
      await axios.delete(`${API}/po/${existing_po_id}?delete_qr_codes=${deleteQR}`, getAuthHeaders());
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/po/upload`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(response.data.message);
      const uploaded = response.data;
      await loadCustomersList();
      setSelectedCustomerId(""); setNewCustomerName("");
      setCustomerDialog({ open: true, poId: uploaded.id, poNumber: uploaded.po_number, mode: "upload" });
      setDupDialog({ open: false, po_number: "", existing_po_id: "", file: null, busy: false, deleteQR: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Delete & re-add failed");
      setDupDialog((d) => ({ ...d, busy: false }));
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const closeReplaceDialog = () => {
    setReplaceDialog({
      open: false, po: null, file: null, previewing: false, preview: null,
      reason: "", allowNumberChange: false, submitting: false,
    });
    if (replaceFileInputRef.current) replaceFileInputRef.current.value = "";
  };

  const submitReplacePO = async () => {
    const { po, file, reason, preview, allowNumberChange } = replaceDialog;
    if (!po || !file) return;
    if (!reason.trim() || reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    if (preview && !preview.number_match && !allowNumberChange) {
      toast.error("PO number differs — tick the confirmation box to proceed");
      return;
    }
    setReplaceDialog((d) => ({ ...d, submitting: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("reason", reason.trim());
      formData.append("allow_number_change", allowNumberChange ? "true" : "false");
      const res = await axios.post(`${API}/po/${po.id}/replace`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data?.message || "PO replaced successfully");
      closeReplaceDialog();
      await fetchData();
      if (viewDialog.open && viewDialog.po?.id === po.id) {
        try {
          const refreshed = await axios.get(`${API}/po/${po.id}`, getAuthHeaders());
          setViewDialog((v) => ({ ...v, po: refreshed.data }));
        } catch (_) { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to replace PO");
      setReplaceDialog((d) => ({ ...d, submitting: false }));
    }
  };


  // ====== BOM Handlers ======
  const openBomDialog = async (po) => {
    setBomDialog({ open: true, po });
    setBomLabel("");
    setBomNotes("");
    await fetchBoms(po.id);
  };

  const closeBomDialog = () => {
    setBomDialog({ open: false, po: null });
    setBoms([]);
    setBomLabel("");
    setBomNotes("");
    if (bomFileInputRef.current) bomFileInputRef.current.value = "";
  };

  const fetchBoms = async (poId) => {
    setBomsLoading(true);
    try {
      const res = await axios.get(`${API}/po/${poId}/boms`, getAuthHeaders());
      setBoms(res.data);
    } catch (error) {
      toast.error("Failed to load BOMs");
    } finally {
      setBomsLoading(false);
    }
  };

  const handleBomUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !bomDialog.po) return;

    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!['.xls', '.xlsx', '.xlsm'].includes(ext)) {
      toast.error("Only Excel files (.xls, .xlsx, .xlsm) are supported");
      return;
    }

    setBomUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (bomLabel.trim()) formData.append('label', bomLabel.trim());
      if (bomNotes.trim()) formData.append('notes', bomNotes.trim());

      const res = await axios.post(`${API}/po/${bomDialog.po.id}/bom/upload`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });

      toast.success(res.data.message);
      setBomLabel("");
      setBomNotes("");
      if (bomFileInputRef.current) bomFileInputRef.current.value = "";
      await fetchBoms(bomDialog.po.id);
      fetchData(); // refresh PO list to update bom_count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload BOM");
    } finally {
      setBomUploading(false);
    }
  };

  const handleBomPreview = (bom) => {
    if (!bom.has_preview_pdf) {
      toast.info("PDF preview not yet generated for this BOM");
      return;
    }
    const token = localStorage.getItem("token");
    const pdfUrl = `${API}/bom/${bom.id}/file/preview?token=${encodeURIComponent(token)}`;
    setBomPreviewDialog({ open: true, bom, pdfUrl });
  };

  const closeBomPreviewDialog = () => {
    setBomPreviewDialog({ open: false, bom: null, pdfUrl: null });
  };

  const handleBomDownload = async (bom) => {
    try {
      const res = await axios.get(`${API}/bom/${bom.id}/file/download`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', bom.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("BOM file downloaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download BOM");
    }
  };

  const handleBomDelete = async (bom) => {
    if (!window.confirm(`Delete BOM "${bom.label || bom.original_filename}"?`)) return;
    try {
      await axios.delete(`${API}/bom/${bom.id}`, getAuthHeaders());
      toast.success("BOM deleted");
      await fetchBoms(bomDialog.po.id);
      fetchData(); // refresh bom_count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete BOM");
    }
  };

  const handleBomCompare = async (bom) => {
    setCompareDialog({ open: true, bom });
    setCompareData(null);
    setCompareError("");
    setCompareLoading(true);
    try {
      const res = await axios.get(`${API}/bom/${bom.id}/compare`, getAuthHeaders());
      setCompareData(res.data);
    } catch (error) {
      setCompareError(error.response?.data?.detail || "Could not compare this BOM");
    } finally {
      setCompareLoading(false);
    }
  };

  const handleAddAliasFromCompare = async (catalogProductId, alias) => {
    try {
      const res = await axios.post(
        `${API}/catalog/alias-health/add-alias`,
        { product_id: catalogProductId, alias },
        getAuthHeaders()
      );
      toast.success(res.data.message || "Alias added");
      if (compareDialog.bom) handleBomCompare(compareDialog.bom); // re-run compare
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add alias");
    }
  };

  const openReviewDialog = async (po) => {
    setReviewDialog({ open: true, po });
    setReviewData(null);
    setReviewLoading(true);
    try {
      const res = await axios.get(`${API}/po/${po.id}/review`, getAuthHeaders());
      setReviewData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load review notes");
    } finally {
      setReviewLoading(false);
    }
  };

  const refreshReview = async (poId) => {
    try {
      const res = await axios.get(`${API}/po/${poId}/review`, getAuthHeaders());
      setReviewData(res.data);
    } catch { /* noop */ }
  };

  const flagPO = async (poId, comment) => {
    try {
      await axios.post(`${API}/po/${poId}/review/flag`, { comment: comment || "" }, getAuthHeaders());
      toast.success("Flagged for review");
      await refreshReview(poId);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Could not flag PO"); }
  };

  const addReviewComment = async (poId, text) => {
    try {
      await axios.post(`${API}/po/${poId}/review/comment`, { text }, getAuthHeaders());
      await refreshReview(poId);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Could not add comment"); }
  };

  const resolveReview = async (poId, comment) => {
    try {
      await axios.post(`${API}/po/${poId}/review/resolve`, { comment: comment || "" }, getAuthHeaders());
      toast.success("Marked as reviewed");
      setReviewDialog({ open: false, po: null });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Could not resolve"); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending: { color: "bg-gray-100 text-gray-700", label: "Pending" },
      partial: { color: "bg-yellow-100 text-yellow-700", label: "Partial" },
      complete: { color: "bg-green-100 text-green-700", label: "Complete" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Production progress bar cell
  const ProgressCell = ({ progress, poId }) => {
    const p = progress || { ordered_total: 0, accepted_total: 0, pending_total: 0, percent: 0, is_over: false, line_complete: 0, line_total: 0 };
    const ordered = p.ordered_total || 0;
    const accepted = p.accepted_total || 0;
    const pct = Math.min(100, p.percent || 0);
    const isOver = !!p.is_over;
    const isComplete = !isOver && ordered > 0 && accepted >= ordered;
    const barColor = isOver
      ? "bg-purple-500"
      : isComplete
      ? "bg-emerald-500"
      : accepted > 0
      ? "bg-amber-500"
      : "bg-gray-300";
    const label = ordered === 0 ? "—" : `${accepted}/${ordered}`;
    const hasPending = ordered > 0 && accepted < ordered;
    return (
      <div className="flex flex-col items-center gap-1 min-w-[110px]" data-testid={`progress-cell-${poId}`}>
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all`}
            style={{ width: `${ordered === 0 ? 0 : pct}%` }}
          />
        </div>
        <div className="flex items-center gap-1 text-[11px] text-gray-600 font-mono">
          <span>{label}</span>
          {ordered > 0 && <span className="text-gray-400">·</span>}
          {ordered > 0 && (
            <span className={isOver ? "text-purple-600 font-semibold" : ""}>{pct}%</span>
          )}
          {isOver && (
            <span title="Over-produced" className="ml-0.5 text-purple-600">
              <AlertTriangle className="w-3 h-3" />
            </span>
          )}
        </div>
        {hasPending && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/safety/pending-production?po_ids=${poId}`); }}
            className="text-[10px] text-emerald-700 hover:text-emerald-800 hover:underline"
            data-testid={`view-pending-${poId}`}
          >
            view pending →
          </button>
        )}
      </div>
    );
  };

  // Production Progress panel inside PO view dialog
  const ProductionProgressPanel = ({ progress, loading, onOpenReport }) => {
    if (loading) {
      return <div className="p-6 text-sm text-gray-500" data-testid="progress-panel-loading">Loading production progress...</div>;
    }
    if (!progress) {
      return <div className="p-6 text-sm text-gray-500" data-testid="progress-panel-empty">No production data yet.</div>;
    }
    const { overall = {}, products = [], reports = [] } = progress;
    const overallOrdered = overall.ordered_total || 0;
    const overallAccepted = overall.accepted_total || 0;
    const overallPct = Math.min(100, overall.percent || 0);
    const overallIsOver = !!overall.is_over;
    const overallBar = overallIsOver
      ? "bg-purple-500"
      : overallAccepted >= overallOrdered && overallOrdered > 0
      ? "bg-emerald-500"
      : overallAccepted > 0
      ? "bg-amber-500"
      : "bg-gray-300";

    const statusPill = (s, isOver) => {
      if (isOver) return { label: "Over-produced", cls: "bg-purple-100 text-purple-700" };
      if (s === "complete") return { label: "Complete", cls: "bg-emerald-100 text-emerald-700" };
      if (s === "in_progress") return { label: "In Progress", cls: "bg-amber-100 text-amber-700" };
      return { label: "Not Started", cls: "bg-gray-100 text-gray-600" };
    };

    const reportStatusPill = (s) => {
      if (s === "approved") return "bg-emerald-100 text-emerald-700";
      if (s === "pending_approval") return "bg-amber-100 text-amber-700";
      return "bg-gray-100 text-gray-600";
    };

    return (
      <div className="space-y-4" data-testid="production-progress-panel">
        {/* Overall summary */}
        <div className="border rounded-lg p-3 bg-gray-50">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-semibold text-gray-800">
              Overall progress
              {overallIsOver && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700" data-testid="over-production-warning">
                  <AlertTriangle className="w-3 h-3" /> Over-produced
                </span>
              )}
            </div>
            <div className="text-xs font-mono text-gray-600">
              {overallAccepted}/{overallOrdered} · <span className={overallIsOver ? "text-purple-600 font-semibold" : ""}>{overallPct}%</span>
              {" · "}
              {overall.line_complete || 0}/{overall.line_total || 0} lines complete
            </div>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full ${overallBar} transition-all`} style={{ width: `${overallOrdered === 0 ? 0 : overallPct}%` }} />
          </div>
        </div>

        {/* Per-line progress table */}
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Order</th>
              <th className="px-3 py-2 text-right">Coated</th>
              <th className="px-3 py-2 text-right">Rework</th>
              <th className="px-3 py-2 text-right">Pending</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {products.map((it, idx) => {
              const pill = statusPill(it.status, it.is_over);
              const pct = Math.min(100, it.percent || 0);
              const bar = it.is_over
                ? "bg-purple-500"
                : it.status === "complete"
                ? "bg-emerald-500"
                : it.accepted_qty > 0
                ? "bg-amber-500"
                : "bg-gray-300";
              return (
                <tr key={`${it.product_code}-${idx}`} className="border-t" data-testid={`progress-row-${it.product_code}`}>
                  <td className="px-3 py-2 font-mono">{it.product_code}</td>
                  <td className="px-3 py-2 text-gray-600">{it.description || '-'}</td>
                  <td className="px-3 py-2 text-right">{it.order_qty}</td>
                  <td className="px-3 py-2 text-right">{it.accepted_qty}</td>
                  <td className="px-3 py-2 text-right">{it.rework_qty}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${it.pending_qty === 0 ? "text-emerald-600" : "text-gray-800"}`}>
                    {it.pending_qty}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pill.cls}`}>{pill.label}</span>
                      <div className="w-24 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${bar}`} style={{ width: `${it.order_qty === 0 ? 0 : pct}%` }} />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
            {products.length === 0 && (
              <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={7}>No line items.</td></tr>
            )}
          </tbody>
        </table>

        {/* Linked reports */}
        <div>
          <div className="text-sm font-semibold text-gray-800 mb-2">
            Linked production reports <span className="text-gray-400 font-normal">({reports.length})</span>
          </div>
          {reports.length === 0 ? (
            <div className="text-xs text-gray-500 px-3 py-3 border rounded-lg bg-gray-50">No approved or pending reports yet.</div>
          ) : (
            <div className="border rounded-lg divide-y">
              {reports.map((r) => (
                <button
                  key={r.id}
                  onClick={() => onOpenReport(r.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-left"
                  data-testid={`linked-report-${r.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-gray-700">{r.report_date}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-600">Batch {r.batch_no || '-'}</span>
                    {r.shade_name && <><span className="text-gray-400">·</span><span className="text-gray-600">{r.shade_name}</span></>}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reportStatusPill(r.status)}`}>
                    {r.status === 'approved' ? 'Approved' : r.status === 'pending_approval' ? 'Pending Approval' : r.status}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };


  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Purchase Orders" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Purchase Orders", active: true, depth: 1 },
        { label: "Production Reports", to: "/safety/production-reports", depth: 1 },
        { label: "Customers", to: "/safety/customers", depth: 1 },
      ]}
    >
      <div data-testid="po-management-page">

      {/* Page header */}
      <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-orange-600" />
          <div>
            <h1 className="text-xl font-bold tracking-tight">Purchase Orders</h1>
            <p className="text-xs text-zinc-500">Upload and manage POs</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canUpload && (
            <Button
              onClick={openMissingCustomerDialog}
              variant="outline"
              size="sm"
              data-testid="bulk-assign-customer-btn"
            >
              <Users className="w-4 h-4 mr-1.5" />
              Assign Customers
            </Button>
          )}
          {flaggedCount > 0 && (
            <Button
              onClick={() => setReviewOnlyFlagged(true)}
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="flagged-summary-chip"
            >
              <Flag className="w-4 h-4 mr-1.5 fill-red-500 text-red-500" />
              {flaggedCount} flagged
            </Button>
          )}
          {canUpload && missingFileCount > 0 && (
            <Button
              onClick={() => setBulkDialog({ open: true })}
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-700"
              data-testid="bulk-reupload-btn"
            >
              <FileUp className="w-4 h-4 mr-1.5" />
              Upload Missing PO Files ({missingFileCount})
            </Button>
          )}
          <Button
            onClick={() => navigate("/safety/pending-production")}
            variant="outline"
            size="sm"
            data-testid="pending-production-nav-btn"
          >
            <BarChart3 className="w-4 h-4 mr-1.5" />
            Pending Production
          </Button>
          {canUpload && (
            <Button
              onClick={handleBackfillPrices}
              variant="outline"
              size="sm"
              disabled={backfilling}
              title="Re-parse stored PO files to fill in missing USD unit prices (fixes POs showing $0 in the Management Report)"
              data-testid="backfill-prices-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${backfilling ? "animate-spin" : ""}`} />
              {backfilling ? "Backfilling…" : "Backfill Prices"}
            </Button>
          )}
        </div>
      </div>

      <div>
        {/* Upload Section */}
        {canUpload && (
          <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload New PO
            </h2>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                uploading ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'
              }`}
              data-testid="po-upload-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx,.xlsm"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="po-file-input"
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                  <p className="text-gray-600">Uploading and parsing PO...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <FileSpreadsheet className="w-10 h-10 text-gray-400" />
                  <p className="font-medium text-gray-700">Click to upload PO file</p>
                  <p className="text-sm text-gray-500">Supports: .xls, .xlsx</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by PO number or vendor..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
              </SelectContent>
            </Select>
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="w-[200px]" data-testid="vendor-filter">
                <SelectValue placeholder="Vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map((vendor) => (
                  <SelectItem key={vendor} value={vendor}>{vendor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="w-[230px]">
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Find product code in POs…"
                  value={productCodeFilter}
                  onChange={(e) => setProductCodeFilter(e.target.value)}
                  className="pl-10"
                  data-testid="product-code-filter"
                />
              </div>
            </div>
            <Button
              type="button"
              variant={reviewOnlyFlagged ? "default" : "outline"}
              onClick={() => setReviewOnlyFlagged((v) => !v)}
              className={reviewOnlyFlagged ? "bg-red-600 hover:bg-red-700" : ""}
              data-testid="filter-flagged-toggle"
            >
              <Flag className={`w-4 h-4 mr-2 ${reviewOnlyFlagged ? "fill-white" : "text-red-500 fill-red-500"}`} />
              Flagged
            </Button>
            <Button
              type="button"
              variant={amendedOnly ? "default" : "outline"}
              onClick={() => setAmendedOnly((v) => !v)}
              className={amendedOnly ? "bg-orange-600 hover:bg-orange-700" : ""}
              data-testid="filter-amended-toggle"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${amendedOnly ? "" : "text-orange-500"}`} />
              Amended
            </Button>
            <ResetFiltersChip
              active={
                searchQuery !== "" ||
                statusFilter !== "all" ||
                vendorFilter !== "all" ||
                productCodeFilter !== "" ||
                amendedOnly ||
                sortColumn !== "order_date" ||
                sortDirection !== "desc"
              }
              onReset={() => {
                setSearchQuery("");
                setStatusFilter("all");
                setVendorFilter("all");
                setProductCodeFilter("");
                setAmendedOnly(false);
                setSortColumn("order_date");
                setSortDirection("desc");
              }}
            />
          </div>
          {productCodeFilter.trim() && (
            <div className="mt-3 text-xs text-gray-500 flex items-center gap-1.5" data-testid="product-filter-note">
              <Package className="w-3.5 h-3.5 text-emerald-600" />
              Showing <b>{sortedPos.length}</b> PO{sortedPos.length === 1 ? "" : "s"} containing
              <span className="font-mono font-medium text-gray-700">"{productCodeFilter.trim()}"</span>
              <span className="text-gray-400">· alias/HT-tolerant match · order, accepted, pending &amp; dispatch shown per matched line</span>
            </div>
          )}
        </div>

        {/* PO Table */}
        <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
            </div>
          ) : pos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No Purchase Orders found</p>
              {canUpload && <p className="text-sm">Upload a PO to get started</p>}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {[
                    { key: "po_number", label: "PO Number", align: "left" },
                    { key: "customer_name", label: "Customer", align: "left" },
                    { key: "ship_to_name", label: "Consignee", align: "left" },
                    { key: "ship_to_location", label: "Ship To", align: "left" },
                    { key: "product_count", label: "Products", align: "center" },
                    ...(productCodeFilter.trim() ? [
                      { key: null, label: "Matched Line", align: "left" },
                      { key: null, label: "Order", align: "center" },
                      { key: null, label: "Accepted", align: "center" },
                      { key: null, label: "Pending", align: "center" },
                      { key: null, label: "Dispatched", align: "center" },
                      { key: null, label: "Dispatch", align: "center" },
                    ] : []),
                    { key: null, label: "Progress", align: "center" },
                    { key: "status", label: "Status", align: "center" },
                    { key: "total_weight_kg", label: "Total Weight", align: "right" },
                    { key: null, label: "BOMs", align: "center" },
                    { key: "order_date", label: "PO Date", align: "center" },
                    { key: "required_date", label: "Required By", align: "center" },
                    { key: null, label: "Actions", align: "right", sticky: true },
                  ].map((col) => (
                    <th
                      key={col.label}
                      className={`px-4 py-3 text-sm font-medium text-gray-600 ${
                        col.align === "center" ? "text-center" : col.align === "right" ? "text-right" : "text-left"
                      } ${col.key ? "cursor-pointer select-none hover:text-gray-900 transition-colors" : ""} ${
                        col.sticky ? "sticky right-0 bg-gray-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] z-10" : ""
                      }`}
                      onClick={() => col.key && handleSort(col.key)}
                      data-testid={col.key ? `sort-${col.key}` : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.key && sortColumn === col.key && (
                          <span className="text-emerald-600">
                            {sortDirection === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedPos.filter((po) => !reviewOnlyFlagged || po.review_flagged).map((po) => (
                  <tr key={po.id} className="group border-b hover:bg-gray-50 cursor-pointer" onClick={() => handlePreviewPO(po)} data-testid={`po-row-${po.id}`}>
                    <td className="px-4 py-3 font-mono font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {po.review_flagged && (
                          <span title="Flagged for review" data-testid={`po-flag-icon-${po.id}`}>
                            <Flag className="w-3.5 h-3.5 text-red-500 fill-red-500" />
                          </span>
                        )}
                        {po.po_number}
                        {po.amendment_count > 0 && (
                          <span
                            title={`Amended ${po.amendment_count} time(s)${po.last_replaced_at ? " · last " + new Date(po.last_replaced_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : ""}`}
                            className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-semibold"
                            data-testid={`po-amended-badge-${po.id}`}
                          >
                            Amended v{po.amendment_count + 1}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-sm" data-testid={`customer-cell-${po.id}`}>
                      {po.customer_name ? (
                        po.customer_name
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openAssignCustomerForPO(po); }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100"
                          data-testid={`assign-customer-${po.id}`}
                        >
                          <AlertTriangle className="w-3 h-3" /> Assign
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{po.ship_to_name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{po.ship_to_location || '-'}</td>
                    <td className="px-4 py-3 text-center">{po.product_count}</td>
                    {productCodeFilter.trim() && (() => {
                      const ml = po.matched_line || {};
                      const dmeta = {
                        fully: { label: "Dispatched", cls: "bg-blue-100 text-blue-700" },
                        partial: { label: "Partial", cls: "bg-amber-100 text-amber-700" },
                        none: { label: "Not dispatched", cls: "bg-zinc-100 text-zinc-600" },
                      }[ml.dispatch_status] || { label: "—", cls: "bg-zinc-100 text-zinc-500" };
                      return (
                        <>
                          <td className="px-4 py-3" data-testid={`matched-code-${po.id}`}>
                            <span className="font-mono text-xs font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{ml.product_code || "-"}</span>
                          </td>
                          <td className="px-4 py-3 text-center font-mono">{ml.order_qty ?? "-"}</td>
                          <td className="px-4 py-3 text-center font-mono">{ml.accepted_qty ?? "-"}</td>
                          <td className={`px-4 py-3 text-center font-mono font-semibold ${ml.pending_qty > 0 ? "text-emerald-700" : "text-gray-400"}`}>{ml.pending_qty ?? "-"}</td>
                          <td className={`px-4 py-3 text-center font-mono ${ml.dispatched_qty > 0 ? "text-blue-700" : "text-gray-400"}`}>{ml.dispatched_qty ?? 0}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${dmeta.cls}`}>{dmeta.label}</span>
                          </td>
                        </>
                      );
                    })()}
                    <td className="px-4 py-3 text-center">
                      <ProgressCell progress={po.production_progress} poId={po.id} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={po.status} />
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" data-testid={`po-weight-${po.id}`}>
                      {po.total_weight_kg > 0 ? (
                        <span className="inline-flex items-center gap-1 font-mono text-sm text-gray-800">
                          {po.total_weight_kg.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                          <span className="text-xs text-gray-400">Kgs</span>
                          {po.weight_has_unmatched && (
                            <span title="Some products have no catalog match — weight is partial" data-testid={`po-weight-partial-${po.id}`}>
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400" data-testid={`po-weight-none-${po.id}`}>
                          {po.weight_has_unmatched ? (
                            <span className="inline-flex items-center gap-1 text-amber-600" title="No catalog match for these products">
                              <AlertTriangle className="w-3.5 h-3.5" /> —
                            </span>
                          ) : "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => { if (canBomView && po.bom_count > 0) { e.stopPropagation(); openBomDialog(po); } }}>
                      {po.bom_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 cursor-pointer hover:bg-blue-100 transition-colors" data-testid={`bom-count-${po.id}`}>
                          <ClipboardList className="w-3 h-3" />
                          {po.bom_count}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400" data-testid={`bom-none-${po.id}`}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">
                      {po.order_date || '-'}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">
                      {po.required_date || '-'}
                    </td>
                    <td className="px-4 py-3 text-right sticky right-0 bg-white group-hover:bg-gray-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] z-10" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`po-actions-${po.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/safety/po/${po.id}/detail`)} data-testid={`open-po-detail-${po.id}`}>
                            <Eye className="w-4 h-4 mr-2" />
                            Open PO Detail
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewPO(po)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Quick View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openReviewDialog(po)} data-testid={`review-po-${po.id}`}>
                            <Flag className={`w-4 h-4 mr-2 ${po.review_flagged ? "text-red-500 fill-red-500" : ""}`} />
                            {po.review_flagged ? `Review notes${po.review_comment_count ? ` (${po.review_comment_count})` : ""}` : "Flag for review"}
                          </DropdownMenuItem>
                          {canDownload && po.has_po_file && (
                            <DropdownMenuItem onClick={() => handleDownloadPOFile(po)} data-testid={`download-po-file-${po.id}`}>
                              <Download className="w-4 h-4 mr-2" />
                              Download PO File
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDownloadBOQ(po)} data-testid={`download-boq-${po.id}`}>
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Download BOQ
                          </DropdownMenuItem>
                          {canBomView && (
                            <DropdownMenuItem onClick={() => openBomDialog(po)} data-testid={`manage-boms-${po.id}`}>
                              <ClipboardList className="w-4 h-4 mr-2" />
                              Manage BOMs {po.bom_count > 0 ? `(${po.bom_count})` : ''}
                            </DropdownMenuItem>
                          )}
                          {canBomUpload && (
                            <DropdownMenuItem onClick={() => navigate(`/safety/po/${po.id}/generate-bom`)} data-testid={`generate-bom-${po.id}`}>
                              <Factory className="w-4 h-4 mr-2" />
                              Generate BOM
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {canGenerateQR && (
                            <DropdownMenuItem onClick={() => handleGenerateQR(po)}>
                              <QrCode className="w-4 h-4 mr-2" />
                              Generate QR
                            </DropdownMenuItem>
                          )}
                          {canReplace && (
                            <DropdownMenuItem onClick={() => startReplacePO(po)} data-testid={`replace-po-${po.id}`}>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Replace PO (amended)
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem
                              onClick={() => setDeleteDialog({ open: true, po })}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete PO
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Status Legend */}
        <div className="mt-4 flex gap-6 text-sm text-gray-500">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-gray-300"></span> Pending - No QR generated
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-yellow-400"></span> Partial - Some products have QR
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span> Complete - All products have QR
          </span>
        </div>
      </div>

      {/* Hidden file input for Replace PO */}
      <input
        ref={replaceFileInputRef}
        type="file"
        accept=".xls,.xlsx,.xlsm"
        className="hidden"
        onChange={onReplaceFileSelected}
        data-testid="replace-po-file-input"
      />

      {/* Duplicate PO — choose Replace vs Delete & re-add */}
      <Dialog open={dupDialog.open} onOpenChange={(open) => !open && !dupDialog.busy && setDupDialog((d) => ({ ...d, open: false }))}>
        <DialogContent className="max-w-2xl" data-testid="dup-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              PO {dupDialog.po_number} already exists
            </DialogTitle>
            <DialogDescription>
              You've changed or amended this PO. Choose how to handle the re-upload — each option affects your production data differently.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            {/* Option 1 — Replace */}
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
              <div className="flex items-center gap-2 font-semibold text-blue-800 text-sm">
                <RefreshCw className="w-4 h-4" /> Replace lines (keep plans &amp; reports)
              </div>
              <ul className="mt-2 text-xs text-slate-600 list-disc pl-5 space-y-1">
                <li><b>Keeps the same PO</b>, so all linked <b>container plans / saved load boards, production reports, packing lists and QR batches stay attached</b>.</li>
                <li>Changed quantities &amp; descriptions are reconciled and <b>cascaded into production reports</b>.</li>
                <li>Removed lines that already have production/dispatch data are kept and flagged “removed in amendment” (nothing is orphaned).</li>
                <li>The previous version is snapshotted to history and the PO is <b>auto-flagged for review</b>.</li>
              </ul>
              <div className="mt-2 flex justify-end">
                <Button size="sm" onClick={dupChooseReplace} disabled={dupDialog.busy} data-testid="dup-choose-replace">
                  Replace lines
                </Button>
              </div>
            </div>

            {/* Option 2 — Delete & re-add */}
            <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
              <div className="flex items-center gap-2 font-semibold text-red-700 text-sm">
                <Trash2 className="w-4 h-4" /> Delete &amp; re-add (fresh PO)
              </div>
              <ul className="mt-2 text-xs text-slate-600 list-disc pl-5 space-y-1">
                <li><b>Permanently deletes</b> the existing PO record, its uploaded file and any linked BOMs, then uploads this file as a <b>brand-new PO</b>.</li>
                <li>Because a new PO id is created, <b>existing container plans / saved load boards and production-report line items linked to the old PO will no longer be linked</b> to it.</li>
                <li>Use this only when the amendment is so large that a clean re-import is easier than reconciling.</li>
              </ul>
              <label className="mt-2 flex items-center gap-2 text-xs text-red-700">
                <Checkbox checked={dupDialog.deleteQR} onCheckedChange={(v) => setDupDialog((d) => ({ ...d, deleteQR: !!v }))} data-testid="dup-delete-qr" />
                Also delete this PO's generated QR batches
              </label>
              <div className="mt-2 flex justify-end">
                <Button size="sm" variant="destructive" onClick={dupChooseDeleteReadd} disabled={dupDialog.busy} data-testid="dup-choose-delete-readd">
                  {dupDialog.busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Working…</> : "Delete & re-add"}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDupDialog((d) => ({ ...d, open: false }))} disabled={dupDialog.busy} data-testid="dup-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace PO Dialog */}
      <Dialog open={replaceDialog.open} onOpenChange={(open) => !open && closeReplaceDialog()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="replace-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-600" />
              Replace PO #{replaceDialog.po?.po_number}
            </DialogTitle>
            <DialogDescription>
              Upload the customer's amended PO. Line items are reconciled and all downstream links
              (production reports, QR codes, pallets, dispatch) are preserved.
            </DialogDescription>
          </DialogHeader>

          {replaceDialog.previewing && (
            <div className="flex items-center gap-2 py-8 justify-center text-gray-500" data-testid="replace-po-previewing">
              <Loader2 className="w-5 h-5 animate-spin" /> Reading amended file…
            </div>
          )}

          {!replaceDialog.previewing && replaceDialog.preview && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 bg-gray-50 rounded-md p-3 border">
                New file: <span className="font-medium">{replaceDialog.preview.parsed?.file_name}</span> ·
                {" "}{replaceDialog.preview.parsed?.product_count} line(s) ·
                {" "}PO Date {replaceDialog.preview.parsed?.order_date || "-"} ·
                {" "}Required {replaceDialog.preview.parsed?.required_date || "-"}
              </div>

              {!replaceDialog.preview.number_match && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="replace-po-number-warning">
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="w-4 h-4" /> PO number mismatch
                  </div>
                  <p className="mt-1">
                    The uploaded file's PO number is <span className="font-mono font-semibold">{replaceDialog.preview.parsed_po_number || "(blank)"}</span>,
                    but this PO is <span className="font-mono font-semibold">{replaceDialog.preview.current_po_number}</span>.
                  </p>
                  <label className="mt-2 flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={replaceDialog.allowNumberChange}
                      onCheckedChange={(v) => setReplaceDialog((d) => ({ ...d, allowNumberChange: !!v }))}
                      data-testid="replace-po-allow-number-change"
                    />
                    <span>I confirm this is the correct amendment (update the PO number too)</span>
                  </label>
                </div>
              )}

              {/* Diff summary chips */}
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-green-100 text-green-700 font-medium" data-testid="replace-diff-added">
                  +{replaceDialog.preview.diff?.added?.length || 0} added
                </span>
                <span className="px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium" data-testid="replace-diff-updated">
                  {replaceDialog.preview.diff?.updated?.length || 0} updated
                </span>
                <span className="px-2 py-1 rounded bg-gray-100 text-gray-600 font-medium" data-testid="replace-diff-unchanged">
                  {replaceDialog.preview.diff?.unchanged?.length || 0} unchanged
                </span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-700 font-medium" data-testid="replace-diff-removed">
                  {replaceDialog.preview.diff?.removed?.length || 0} removed
                </span>
              </div>

              {/* Added */}
              {replaceDialog.preview.diff?.added?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-1">New line items</p>
                  <ul className="text-sm space-y-1">
                    {replaceDialog.preview.diff.added.map((a, i) => (
                      <li key={`add-${i}`} className="flex gap-2">
                        <span className="font-mono">{a.product_code}</span>
                        <span className="text-gray-500">qty {a.qty ?? "-"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Updated */}
              {replaceDialog.preview.diff?.updated?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-700 mb-1">Changed line items</p>
                  <ul className="text-sm space-y-1">
                    {replaceDialog.preview.diff.updated.map((u, i) => (
                      <li key={`upd-${i}`} className="flex flex-wrap gap-2">
                        <span className="font-mono">{u.product_code}</span>
                        {Object.entries(u.diff || {}).map(([k, v]) => (
                          <span key={k} className="text-gray-600">
                            {k}: <span className="line-through text-gray-400">{String(v[0] ?? "-")}</span> → <span className="font-medium">{String(v[1] ?? "-")}</span>
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Removed */}
              {replaceDialog.preview.diff?.removed?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-1">Removed in this amendment</p>
                  <ul className="text-sm space-y-1">
                    {replaceDialog.preview.diff.removed.map((r, i) => (
                      <li key={`rem-${i}`} className="flex flex-wrap items-center gap-2">
                        <span className="font-mono">{r.product_code}</span>
                        {r.has_downstream ? (
                          <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">
                            retained (has {[
                              r.report_count ? `${r.report_count} report(s)` : null,
                              r.qr_count ? `${r.qr_count} QR` : null,
                              r.pallet_count ? `${r.pallet_count} pallet(s)` : null,
                            ].filter(Boolean).join(", ")})
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs">will be dropped</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-500 mt-1">
                    Lines with existing production/dispatch data are kept and flagged "removed in amendment" so nothing is orphaned.
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="replace-reason" className="text-sm">Reason for replacement (min 10 chars)</Label>
                <Textarea
                  id="replace-reason"
                  value={replaceDialog.reason}
                  onChange={(e) => setReplaceDialog((d) => ({ ...d, reason: e.target.value }))}
                  placeholder="e.g. Customer resent PO with revised quantities on 2026-06-10"
                  className="mt-1"
                  data-testid="replace-po-reason"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeReplaceDialog} data-testid="replace-po-cancel">Cancel</Button>
            <Button
              onClick={submitReplacePO}
              disabled={replaceDialog.previewing || replaceDialog.submitting || !replaceDialog.preview}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="replace-po-confirm"
            >
              {replaceDialog.submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Replacing…</>) : "Replace PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* View PO Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, po: null })}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>PO #{viewDialog.po?.po_number}</DialogTitle>
            <DialogDescription>
              {viewDialog.po?.ship_to_name || viewDialog.po?.vendor_name} • {viewDialog.po?.ship_to_location || ''} • PO Date: {viewDialog.po?.order_date || '-'} • Required By: {viewDialog.po?.required_date || '-'}
            </DialogDescription>
          </DialogHeader>

          {/* File Status */}
          <div className="flex items-center gap-3 p-3 rounded-lg border bg-gray-50">
            <FileSpreadsheet className="w-5 h-5 text-gray-500" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                Original PO File
              </p>
              {viewDialog.po?.po_file_key ? (
                <p className="text-xs text-green-600">File stored • {viewDialog.po?.file_name}</p>
              ) : (
                <p className="text-xs text-amber-600">Original file not available — use bulk upload to restore</p>
              )}
            </div>
            {canDownload && viewDialog.po?.po_file_key && (
              <Button size="sm" variant="outline" onClick={() => handleDownloadPOFile(viewDialog.po)} data-testid="view-dialog-download-po-file">
                <Download className="w-3 h-3 mr-1" />
                Download
              </Button>
            )}
          </div>

          <div className="max-h-[450px] overflow-y-auto">
            <Tabs defaultValue="items" className="w-full">
              <TabsList className="mb-2">
                <TabsTrigger value="items" data-testid="tab-items">Items</TabsTrigger>
                <TabsTrigger value="progress" data-testid="tab-production-progress">Production Progress</TabsTrigger>
              </TabsList>

              <TabsContent value="items">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Product Code</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      {canEditLineItem && <th className="px-3 py-2 text-right w-12"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {viewDialog.po?.products?.map((product, index) => (
                      <tr key={index} className="border-t group">
                        <td className="px-3 py-2 font-mono">{product.product_code}</td>
                        <td className="px-3 py-2 text-gray-600">{product.description || '-'}</td>
                        <td className="px-3 py-2 text-right">{product.qty}</td>
                        {canEditLineItem && (
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 opacity-60 group-hover:opacity-100"
                                onClick={() => openEditLineDialog(viewDialog.po, product)}
                                data-testid={`edit-line-item-${product.product_code}`}
                                title="Correct this line item"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 opacity-60 group-hover:opacity-100 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteLineItem(viewDialog.po, product)}
                                data-testid={`delete-line-item-${product.product_code}`}
                                title="Remove this line item"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {canEditLineItem && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAddLineDialog(viewDialog.po)}
                      data-testid="add-line-item-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Row
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="progress">
                <ProductionProgressPanel
                  progress={viewDialog.progress}
                  loading={viewDialog.progressLoading}
                  onOpenReport={(rid) => navigate(`/safety/production-reports/${rid}`)}
                />
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ open: false, po: null })}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownloadBOQ(viewDialog.po)}
              data-testid="view-dialog-download-boq"
            >
              <Download className="w-4 h-4 mr-2" />
              Download BOQ
            </Button>
            {canGenerateQR && (
              <Button onClick={() => {
                setViewDialog({ open: false, po: null });
                handleGenerateQR(viewDialog.po);
              }}>
                <QrCode className="w-4 h-4 mr-2" />
                Generate QR Codes
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Line Item Dialog */}
      <Dialog open={editLineDialog.open} onOpenChange={(open) => !open && !editLineDialog.submitting && closeEditLineDialog()}>
        <DialogContent className="max-w-2xl" data-testid="edit-line-item-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Correct PO Line Item
            </DialogTitle>
            <DialogDescription>
              PO #{editLineDialog.po?.po_number} · Original code: <span className="font-mono font-medium">{editLineDialog.line?.product_code}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {/* Impact Preview */}
            <div className="rounded-md border bg-amber-50 border-amber-200 p-3">
              <div className="flex items-center gap-2 text-amber-900 font-medium text-sm mb-2">
                <AlertTriangle className="w-4 h-4" />
                Impact Preview
              </div>
              {editLineDialog.impactLoading ? (
                <div className="text-sm text-amber-800">Loading impact…</div>
              ) : editLineDialog.impact ? (
                <div className="text-sm space-y-1.5 text-amber-900">
                  {editLineDialog.impact.totals.report_count === 0 ? (
                    <div className="text-amber-800">No production reports reference this line item — clean correction.</div>
                  ) : (
                    <>
                      <div>
                        This change will cascade into <strong>{editLineDialog.impact.totals.report_count}</strong> production report{editLineDialog.impact.totals.report_count === 1 ? "" : "s"}.
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div className="bg-white/60 rounded px-2 py-1.5 text-xs">
                          <div className="text-gray-500">Approved</div>
                          <div className="font-semibold">{editLineDialog.impact.production_reports.approved?.length || 0}</div>
                        </div>
                        <div className="bg-white/60 rounded px-2 py-1.5 text-xs">
                          <div className="text-gray-500">Pending Approval</div>
                          <div className="font-semibold">{editLineDialog.impact.production_reports.pending_approval?.length || 0}</div>
                        </div>
                        <div className="bg-white/60 rounded px-2 py-1.5 text-xs">
                          <div className="text-gray-500">Drafts</div>
                          <div className="font-semibold">{editLineDialog.impact.production_reports.draft?.length || 0}</div>
                        </div>
                      </div>
                      <div className="text-xs mt-1">
                        Total accepted qty already reported under <span className="font-mono">{editLineDialog.line?.product_code}</span>:{" "}
                        <strong>{editLineDialog.impact.totals.accepted_qty}</strong> (will move to new code)
                      </div>
                    </>
                  )}
                  {editLineDialog.impact.qr_codes?.count > 0 && (
                    <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                      ⚠️ {editLineDialog.impact.qr_codes.count} QR code{editLineDialog.impact.qr_codes.count === 1 ? "" : "s"} were already generated with the old product code.
                      Already-printed QR labels will <strong>not</strong> be auto-updated — handle physical labels separately.
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-amber-800">Impact preview unavailable.</div>
              )}
            </div>

            {/* Edit form */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-1">
                <Label className="text-xs text-gray-600">New Product Code <span className="text-red-500">*</span></Label>
                <Input
                  value={editLineDialog.form.new_product_code}
                  onChange={(e) => setEditLineDialog((d) => ({ ...d, form: { ...d.form, new_product_code: e.target.value } }))}
                  className="font-mono"
                  data-testid="edit-line-new-code"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-xs text-gray-600">Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  value={editLineDialog.form.new_qty}
                  onChange={(e) => setEditLineDialog((d) => ({ ...d, form: { ...d.form, new_qty: e.target.value } }))}
                  data-testid="edit-line-new-qty"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-gray-600">Description</Label>
                <Input
                  value={editLineDialog.form.new_description}
                  onChange={(e) => setEditLineDialog((d) => ({ ...d, form: { ...d.form, new_description: e.target.value } }))}
                  data-testid="edit-line-new-desc"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-gray-600">
                  Reason <span className="text-red-500">*</span> <span className="text-gray-400">(min 10 chars; stored in audit log)</span>
                </Label>
                <Input
                  placeholder="e.g. Typo in vendor's PO file — verified with customer over email"
                  value={editLineDialog.form.reason}
                  onChange={(e) => setEditLineDialog((d) => ({ ...d, form: { ...d.form, reason: e.target.value } }))}
                  data-testid="edit-line-reason"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeEditLineDialog} disabled={editLineDialog.submitting} data-testid="edit-line-cancel">
              Cancel
            </Button>
            <Button onClick={submitEditLineItem} disabled={editLineDialog.submitting || editLineDialog.impactLoading} data-testid="edit-line-confirm">
              {editLineDialog.submitting ? "Applying…" : "Confirm correction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Add Line Item Dialog */}
      <Dialog open={addLineDialog.open} onOpenChange={(open) => !open && !addLineDialog.submitting && closeAddLineDialog()}>
        <DialogContent className="max-w-lg" data-testid="add-line-item-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Line Item
            </DialogTitle>
            <DialogDescription>
              PO #{addLineDialog.po?.po_number}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-600">
                Product Code <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="e.g. GR-8 HT"
                value={addLineDialog.form.product_code}
                onChange={(e) => setAddLineDialog((d) => ({ ...d, form: { ...d.form, product_code: e.target.value } }))}
                data-testid="add-line-product-code"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Qty <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                value={addLineDialog.form.qty}
                onChange={(e) => setAddLineDialog((d) => ({ ...d, form: { ...d.form, qty: e.target.value } }))}
                data-testid="add-line-qty"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-gray-600">Description</Label>
              <Input
                placeholder="Optional product description"
                value={addLineDialog.form.description}
                onChange={(e) => setAddLineDialog((d) => ({ ...d, form: { ...d.form, description: e.target.value } }))}
                data-testid="add-line-description"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Rate</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Optional"
                value={addLineDialog.form.rate}
                onChange={(e) => setAddLineDialog((d) => ({ ...d, form: { ...d.form, rate: e.target.value } }))}
                data-testid="add-line-rate"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeAddLineDialog} disabled={addLineDialog.submitting} data-testid="add-line-cancel">
              Cancel
            </Button>
            <Button onClick={submitAddLineItem} disabled={addLineDialog.submitting} data-testid="add-line-confirm">
              {addLineDialog.submitting ? "Adding…" : "Add line item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Preview PO Dialog */}
      <Dialog open={previewDialog.open} onOpenChange={(open) => !open && closePreviewDialog()}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] flex flex-col" data-testid="po-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              PO #{previewDialog.po?.po_number}
            </DialogTitle>
            <DialogDescription>
              {previewDialog.po?.file_name || 'Preview'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden border rounded-lg bg-gray-100 min-h-0" style={{ height: 'calc(95vh - 120px)' }} data-testid="po-preview-content">
            {previewDialog.loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                <span className="ml-3 text-gray-500">Loading preview...</span>
              </div>
            ) : previewDialog.pdfUrl ? (
              <iframe
                src={previewDialog.pdfUrl}
                className="w-full h-full border-0"
                title={`PO Preview - ${previewDialog.po?.po_number}`}
                data-testid="po-preview-iframe"
              />
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePreviewDialog}>
              Close
            </Button>
            {canDownload && previewDialog.po?.has_po_file && (
              <Button onClick={() => handleDownloadPOFile(previewDialog.po)} data-testid="preview-download-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Excel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete PO Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => {
        if (!open) {
          setDeleteDialog({ open: false, po: null });
          setDeleteConfirmText("");
          setDeleteQRCodes(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Purchase Order
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete PO #{deleteDialog.po?.po_number}?
            </DialogDescription>
          </DialogHeader>

          {deleteDialog.po?.qr_batch_count > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 mb-3">
                This PO has {deleteDialog.po.qr_batch_count} QR batch(es) generated.
              </p>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="delete-qr"
                  checked={deleteQRCodes}
                  onCheckedChange={setDeleteQRCodes}
                  data-testid="delete-qr-checkbox"
                />
                <Label htmlFor="delete-qr" className="text-sm text-yellow-800 cursor-pointer">
                  Also delete associated QR codes from cloud storage
                </Label>
              </div>
              <p className="text-xs text-yellow-600 mt-2">
                (This cannot be undone)
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="confirm-delete">Type "DELETE" to confirm:</Label>
            <Input
              id="confirm-delete"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-2"
              data-testid="delete-confirm-input"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialog({ open: false, po: null });
                setDeleteConfirmText("");
                setDeleteQRCodes(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePO}
              disabled={deleteConfirmText !== "DELETE" || deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Deleting..." : "Delete PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reupload Dialog */}
      <Dialog open={bulkDialog.open} onOpenChange={(open) => !open && closeBulkDialog()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5" />
              Bulk Upload Missing PO Files
            </DialogTitle>
            <DialogDescription>
              Upload original PO Excel files for existing POs. The system will auto-match each file to a PO by extracting the PO number.
            </DialogDescription>
          </DialogHeader>

          {!bulkResults ? (
            <div className="space-y-4">
              <div
                onClick={() => bulkFileInputRef.current?.click()}
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-emerald-500 hover:bg-emerald-50"
                data-testid="bulk-upload-zone"
              >
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".xls,.xlsx,.xlsm"
                  multiple
                  onChange={handleBulkFilesSelected}
                  className="hidden"
                  data-testid="bulk-file-input"
                />
                <FileSpreadsheet className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="font-medium text-gray-700">Click to select PO Excel files</p>
                <p className="text-sm text-gray-500 mt-1">Select multiple files at once (.xls, .xlsx)</p>
              </div>

              {bulkFiles.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">{bulkFiles.length} file(s) selected:</p>
                  <div className="max-h-[150px] overflow-y-auto space-y-1">
                    {bulkFiles.map((file, idx) => (
                      <div key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                        <FileSpreadsheet className="w-3 h-3 text-gray-400" />
                        {file.name}
                        <span className="text-gray-400 text-xs">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-700">{bulkResults.summary.matched}</p>
                  <p className="text-xs text-green-600">Matched</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-blue-700">{bulkResults.summary.already_has_file}</p>
                  <p className="text-xs text-blue-600">Already Has File</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-amber-700">{bulkResults.summary.unmatched}</p>
                  <p className="text-xs text-amber-600">Unmatched</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-red-700">{bulkResults.summary.errors}</p>
                  <p className="text-xs text-red-600">Errors</p>
                </div>
              </div>

              {/* Details */}
              <div className="max-h-[250px] overflow-y-auto space-y-2">
                {bulkResults.details.matched.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-green-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                    <span className="font-mono font-medium">{item.po_number}</span>
                    <span className="text-gray-400">&larr;</span>
                    <span className="text-gray-600 truncate">{item.filename}</span>
                  </div>
                ))}
                {bulkResults.details.already_has_file.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-blue-50 rounded">
                    <FileWarning className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="font-mono font-medium">{item.po_number}</span>
                    <span className="text-blue-600 text-xs">already has file — skipped</span>
                  </div>
                ))}
                {bulkResults.details.unmatched.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-amber-50 rounded">
                    <XCircle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="text-gray-600 truncate">{item.filename}</span>
                    <span className="text-amber-600 text-xs">{item.reason}</span>
                  </div>
                ))}
                {bulkResults.details.errors.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-red-50 rounded">
                    <XCircle className="w-4 h-4 text-red-600 shrink-0" />
                    <span className="text-gray-600 truncate">{item.filename}</span>
                    <span className="text-red-600 text-xs">{item.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            {!bulkResults ? (
              <>
                <Button variant="outline" onClick={closeBulkDialog}>Cancel</Button>
                <Button
                  onClick={handleBulkUpload}
                  disabled={bulkFiles.length === 0 || bulkUploading}
                  data-testid="bulk-upload-submit"
                >
                  {bulkUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Match ({bulkFiles.length} files)
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={closeBulkDialog}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOM Management Dialog */}
      <Dialog open={bomDialog.open} onOpenChange={(open) => !open && closeBomDialog()}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="bom-management-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              BOMs — PO #{bomDialog.po?.po_number}
            </DialogTitle>
            <DialogDescription>
              Manage Bill of Materials files for this Purchase Order
            </DialogDescription>
          </DialogHeader>

          {/* Upload Section */}
          {canBomUpload && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Upload New BOM
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bom-label" className="text-xs text-gray-500">Label (optional)</Label>
                  <Input
                    id="bom-label"
                    placeholder="e.g. Structural BOM, Revised v2"
                    value={bomLabel}
                    onChange={(e) => setBomLabel(e.target.value)}
                    className="mt-1"
                    data-testid="bom-label-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bom-notes" className="text-xs text-gray-500">Notes (optional)</Label>
                  <Input
                    id="bom-notes"
                    placeholder="Any additional notes..."
                    value={bomNotes}
                    onChange={(e) => setBomNotes(e.target.value)}
                    className="mt-1"
                    data-testid="bom-notes-input"
                  />
                </div>
              </div>
              <div
                onClick={() => !bomUploading && bomFileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  bomUploading ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'
                }`}
                data-testid="bom-upload-zone"
              >
                <input
                  ref={bomFileInputRef}
                  type="file"
                  accept=".xls,.xlsx,.xlsm"
                  onChange={handleBomUpload}
                  className="hidden"
                  data-testid="bom-file-input"
                />
                {bomUploading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
                    <span className="text-sm text-gray-600">Uploading & generating preview...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">Click to select BOM Excel file (.xls, .xlsx)</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* BOM List */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {bomsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                <span className="ml-2 text-gray-500 text-sm">Loading BOMs...</span>
              </div>
            ) : boms.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No BOMs uploaded for this PO yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {boms.map((bom) => (
                  <div key={bom.id} className={`border rounded-lg p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${bom.source === 'generated' ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-gray-300'}`} data-testid={`bom-item-${bom.id}`}>
                    <div className="shrink-0">
                      <File className={`w-8 h-8 ${bom.source === 'generated' ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">
                          {bom.label || bom.original_filename}
                        </p>
                        {bom.source === 'generated' ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 border border-blue-200 whitespace-nowrap">Auto-Generated</span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">Uploaded</span>
                        )}
                        {bom.bom_type === 'vendor' && bom.vendor_name && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-cyan-100 text-cyan-700 border border-cyan-200 whitespace-nowrap">{bom.vendor_name}</span>
                        )}
                        {bom.bom_type === 'inhouse' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-teal-100 text-teal-700 border border-teal-200 whitespace-nowrap">Inhouse</span>
                        )}
                      </div>
                      {bom.label && (
                        <p className="text-xs text-gray-500 truncate">{bom.original_filename}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span>{formatFileSize(bom.file_size)}</span>
                        <span>{bom.uploaded_by}</span>
                        <span>{new Date(bom.uploaded_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                        {bom.notes && <span className="text-gray-500 italic truncate max-w-[150px]">{bom.notes}</span>}
                        {bom.source === 'generated' && bom.product_count > 0 && <span>{bom.product_count} products</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {bom.source !== 'generated' && canBomView && (
                        <Button size="sm" variant="ghost" onClick={() => handleBomCompare(bom)} title="Compare with Auto-BOM" className="text-emerald-600 hover:text-emerald-700" data-testid={`bom-compare-${bom.id}`}>
                          <GitCompareArrows className="w-4 h-4" />
                        </Button>
                      )}
                      {bom.has_preview_pdf && canBomView && (
                        <Button size="sm" variant="ghost" onClick={() => handleBomPreview(bom)} title="Preview PDF" data-testid={`bom-preview-${bom.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      {canBomDownload && (
                        <Button size="sm" variant="ghost" onClick={() => handleBomDownload(bom)} title="Download Excel" data-testid={`bom-download-${bom.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      {canBomDelete && (
                        <Button size="sm" variant="ghost" onClick={() => handleBomDelete(bom)} title="Delete BOM" className="text-red-500 hover:text-red-700" data-testid={`bom-delete-${bom.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeBomDialog} data-testid="bom-dialog-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOM Preview Dialog */}
      <Dialog open={bomPreviewDialog.open} onOpenChange={(open) => !open && closeBomPreviewDialog()}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] flex flex-col" data-testid="bom-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              BOM Preview — {bomPreviewDialog.bom?.label || bomPreviewDialog.bom?.original_filename}
            </DialogTitle>
            <DialogDescription>
              {bomPreviewDialog.bom?.original_filename}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden border rounded-lg bg-gray-100 min-h-0" style={{ height: 'calc(95vh - 120px)' }} data-testid="bom-preview-content">
            {bomPreviewDialog.pdfUrl ? (
              <iframe
                src={bomPreviewDialog.pdfUrl}
                className="w-full h-full border-0"
                title={`BOM Preview - ${bomPreviewDialog.bom?.original_filename}`}
                data-testid="bom-preview-iframe"
              />
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeBomPreviewDialog}>
              Close
            </Button>
            {canBomDownload && bomPreviewDialog.bom && (
              <Button onClick={() => handleBomDownload(bomPreviewDialog.bom)} data-testid="bom-preview-download-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Excel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOM Comparison Dialog */}
      <BomCompareDialog
        open={compareDialog.open}
        bom={compareDialog.bom}
        data={compareData}
        loading={compareLoading}
        error={compareError}
        canManageCatalog={hasPermission("catalog.manage")}
        onAddAlias={handleAddAliasFromCompare}
        onFlagPo={flagPO}
        onClose={() => setCompareDialog({ open: false, bom: null })}
      />

      {/* PO Review Notes Dialog */}
      <PoReviewDialog
        open={reviewDialog.open}
        po={reviewDialog.po}
        data={reviewData}
        loading={reviewLoading}
        onFlag={flagPO}
        onComment={addReviewComment}
        onResolve={resolveReview}
        onClose={() => setReviewDialog({ open: false, po: null })}
      />

      {/* Customer Assignment Dialog (after upload / edit) */}
      <Dialog open={customerDialog.open} onOpenChange={(o) => !o && setCustomerDialog({ open: false, poId: null, poNumber: null, mode: "upload" })}>
        <DialogContent data-testid="customer-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign Customer for PO {customerDialog.poNumber}</DialogTitle>
            <DialogDescription>
              Select an existing customer or create a new one. Customer names come from your Customer Master.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Existing Customer</Label>
              <Select value={selectedCustomerId} onValueChange={(v) => { setSelectedCustomerId(v); setNewCustomerName(""); }}>
                <SelectTrigger data-testid="customer-select"><SelectValue placeholder="Choose customer" /></SelectTrigger>
                <SelectContent>
                  {customersList.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-center text-gray-400 text-xs">— or —</div>
            <div>
              <Label>Create New Customer</Label>
              <Input
                placeholder="Customer name"
                value={newCustomerName}
                onChange={(e) => { setNewCustomerName(e.target.value); if (e.target.value) setSelectedCustomerId(""); }}
                data-testid="new-customer-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerDialog({ open: false, poId: null, poNumber: null, mode: "upload" })}>
              {customerDialog.mode === "upload" ? "Skip (assign later)" : "Cancel"}
            </Button>
            <Button onClick={saveCustomerAssignment} disabled={savingCustomer} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-customer-assignment-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Missing Customer Dialog */}
      <Dialog open={missingCustomerDialog} onOpenChange={setMissingCustomerDialog}>
        <DialogContent className="max-w-3xl" data-testid="missing-customer-dialog">
          <DialogHeader>
            <DialogTitle>POs Missing Customer</DialogTitle>
            <DialogDescription>Click a PO below to assign a customer.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 text-left">PO No</th>
                  <th className="p-2 text-left">Ship To</th>
                  <th className="p-2 text-left">Order Date</th>
                  <th className="p-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {missingCustomerPOs.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-gray-500" data-testid="no-missing-customer">All POs have customers assigned 🎉</td></tr>}
                {missingCustomerPOs.map(po => (
                  <tr key={po.id} className="border-t" data-testid={`missing-customer-row-${po.id}`}>
                    <td className="p-2 font-medium">{po.po_number}</td>
                    <td className="p-2 text-gray-600">{po.ship_to_name || "-"}</td>
                    <td className="p-2 text-gray-500">{po.order_date || "-"}</td>
                    <td className="p-2">
                      <Button size="sm" variant="outline" onClick={() => { setMissingCustomerDialog(false); openAssignCustomerForPO(po); }} data-testid={`missing-assign-${po.id}`}>
                        Assign
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingCustomerDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </SafetyShell>
  );
}

function PoReviewDialog({ open, po, data, loading, onFlag, onComment, onResolve, onClose }) {
  const [draft, setDraft] = useState("");
  const flagged = !!data?.review_flagged;
  const comments = data?.comments || [];

  const fmtTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const submit = async () => {
    const text = draft.trim();
    if (!text || !po) return;
    if (!flagged) await onFlag(po.id, text);   // first comment also flags
    else await onComment(po.id, text);
    setDraft("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg flex flex-col max-h-[85vh]" data-testid="po-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className={`w-5 h-5 ${flagged ? "text-red-500 fill-red-500" : "text-gray-400"}`} />
            Review Notes — PO {po?.po_number}
          </DialogTitle>
          <DialogDescription>
            {flagged
              ? <span className="text-red-600 font-medium">Flagged for review{data?.flagged_by_name ? ` by ${data.flagged_by_name}` : ""}</span>
              : "Add a comment to flag this PO for review with your team."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-2 py-1" data-testid="po-review-thread">
          {loading && (
            <div className="flex items-center justify-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          )}
          {!loading && comments.length === 0 && (
            <div className="text-center text-sm text-gray-400 py-8" data-testid="po-review-empty">No comments yet.</div>
          )}
          {!loading && comments.map((c) => (
            <div key={c.id} className="rounded-lg border bg-gray-50 px-3 py-2" data-testid={`po-review-comment-${c.id}`}>
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="text-xs font-semibold text-gray-700">{c.author_name}</span>
                <span className="text-[11px] text-gray-400">{fmtTime(c.created_at)}</span>
              </div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">{c.text}</div>
            </div>
          ))}
        </div>

        <div className="border-t pt-3 space-y-2">
          <div className="flex items-end gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
              placeholder={flagged ? "Add a comment… (Ctrl/⌘+Enter)" : "Describe the discrepancy… (this will flag the PO)"}
              className="min-h-[60px] text-sm"
              data-testid="po-review-input"
            />
            <Button onClick={submit} disabled={!draft.trim()} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="po-review-submit">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center justify-between">
            {flagged ? (
              <Button variant="outline" size="sm" onClick={() => onResolve(po.id, "")} className="text-emerald-700 border-emerald-200" data-testid="po-review-resolve">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark reviewed (clear flag)
              </Button>
            ) : <span className="text-xs text-gray-400">Posting a comment flags this PO.</span>}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="po-review-close">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_META = {
  match: { label: "In sync", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  mismatch: { label: "Differs", cls: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  only_uploaded: { label: "Only in uploaded", cls: "bg-rose-100 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  only_auto: { label: "Only in auto", cls: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500" },
};

const fmtVal = (v, kind) => {
  if (v === null || v === undefined || v === "") return "—";
  if (kind === "num" && typeof v === "number") {
    return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 3 });
  }
  return String(v);
};

function BomCompareDialog({ open, bom, data, loading, error, canManageCatalog, onAddAlias, onFlagPo, onClose }) {
  const [expanded, setExpanded] = useState({});
  const [linkSel, setLinkSel] = useState({});
  const [flagNote, setFlagNote] = useState("");
  const [flagged, setFlagged] = useState(false);
  useEffect(() => { setFlagged(false); setFlagNote(""); }, [data?.bom_id]);
  const toggle = (code) => setExpanded((p) => ({ ...p, [code]: !p[code] }));
  const s = data?.summary;
  const autoOnly = (data?.products || []).filter((p) => p.status === "only_auto" && p.catalog_product_id);

  const handleFlag = async () => {
    if (!data?.po_id) return;
    const summary = s
      ? `Flagged from BOM compare (${data.filename || bom?.original_filename || "uploaded BOM"}): ${s.mismatched} differing, ${s.only_uploaded} only-in-uploaded, ${s.only_auto} only-in-auto.`
      : "Flagged from BOM compare.";
    const note = flagNote.trim() ? `${flagNote.trim()}\n\n${summary}` : summary;
    await onFlagPo(data.po_id, note);
    setFlagged(true);
    setFlagNote("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] max-h-[92vh] flex flex-col" data-testid="bom-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-emerald-600" />
            Compare BOM — Uploaded vs Auto-Generated
          </DialogTitle>
          <DialogDescription>
            {bom?.original_filename} · PO {data?.po_number || bom?.po_number} · Auto BOM uses catalog defaults (inhouse)
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 pr-1">
          {loading && (
            <div className="flex items-center justify-center py-24 text-gray-400" data-testid="bom-compare-loading">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Comparing…
            </div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="bom-compare-error">
              <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
              <div className="font-medium text-gray-800">Couldn't compare this BOM</div>
              <div className="text-sm text-gray-500 max-w-md mt-1">{error}</div>
            </div>
          )}

          {!loading && !error && data && (
            <div className="space-y-4">
              {/* Summary */}
              <div className={`rounded-lg border p-3 flex items-center gap-2 ${s.in_sync ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="bom-compare-summary">
                {s.in_sync ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                <span className="font-medium text-gray-800">
                  {s.in_sync ? "Uploaded BOM matches the auto-generated BOM." : `${s.mismatched} differing · ${s.only_uploaded} only in uploaded · ${s.only_auto} only in auto`}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Products (uploaded / auto)", val: `${s.products_uploaded} / ${s.products_auto}` },
                  { label: "Matched", val: s.matched, cls: "text-emerald-600" },
                  { label: "Total Qty (up / auto)", val: `${fmtVal(s.qty_uploaded, "num")} / ${fmtVal(s.qty_auto, "num")}`, warn: s.qty_uploaded !== s.qty_auto },
                  { label: "Total Weight Kg (up / auto)", val: `${fmtVal(s.weight_uploaded, "num")} / ${fmtVal(s.weight_auto, "num")}`, warn: Math.abs((s.weight_uploaded || 0) - (s.weight_auto || 0)) > 0.5 },
                ].map((k, i) => (
                  <div key={i} className="bg-white rounded-lg border p-3" data-testid={`bom-compare-kpi-${i}`}>
                    <div className={`text-lg font-bold ${k.warn ? "text-amber-600" : k.cls || "text-gray-800"}`}>{k.val}</div>
                    <div className="text-xs text-gray-500">{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Products */}
              <div className="space-y-2">
                {data.products.map((p) => {
                  const meta = STATUS_META[p.status] || STATUS_META.mismatch;
                  const hasDetail = (p.fields && p.fields.length > 0) || (p.components && p.components.rows.length > 0);
                  const isOpen = !!expanded[p.product_code];
                  return (
                    <div key={p.product_code} className="border rounded-lg overflow-hidden" data-testid={`bom-compare-product-${p.product_code}`}>
                      <button
                        type="button"
                        onClick={() => hasDetail && toggle(p.product_code)}
                        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
                        data-testid={`bom-compare-toggle-${p.product_code}`}
                      >
                        {hasDetail ? (isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />) : <span className="w-4" />}
                        <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                        <span className="font-mono font-medium text-sm">{p.product_code}</span>
                        <span className="text-xs text-gray-500 truncate flex-1">{p.description}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                      </button>

                      {p.status === "only_uploaded" && canManageCatalog && autoOnly.length > 0 && (
                        <div className="px-3 py-2 bg-rose-50/50 border-t flex items-center gap-2 flex-wrap" data-testid={`bom-compare-link-${p.product_code}`}>
                          <span className="text-xs text-rose-700">Not in auto BOM — add <span className="font-mono font-medium">{p.product_code}</span> as an alias of:</span>
                          <select
                            className="h-7 text-xs border rounded px-1.5 bg-white font-mono"
                            value={linkSel[p.product_code] || autoOnly[0].catalog_product_id}
                            onChange={(e) => setLinkSel((m) => ({ ...m, [p.product_code]: e.target.value }))}
                            data-testid={`bom-compare-link-select-${p.product_code}`}
                          >
                            {autoOnly.map((a) => (
                              <option key={a.catalog_product_id} value={a.catalog_product_id}>
                                {a.product_code}{a.description ? ` — ${a.description}` : ""}
                              </option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs"
                            onClick={() => onAddAlias(linkSel[p.product_code] || autoOnly[0].catalog_product_id, p.product_code)}
                            data-testid={`bom-compare-link-btn-${p.product_code}`}
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Add as alias
                          </Button>
                        </div>
                      )}

                      {isOpen && (
                        <div className="p-3 space-y-3">
                          {p.fields && p.fields.length > 0 && (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-xs text-gray-500 text-left">
                                  <th className="py-1 font-medium">Field</th>
                                  <th className="py-1 font-medium">Uploaded</th>
                                  <th className="py-1 font-medium">Auto</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.fields.map((f, i) => (
                                  <tr key={i} className={f.match ? "" : "bg-amber-50"}>
                                    <td className="py-1 pr-3 text-gray-600">{f.label}</td>
                                    <td className={`py-1 pr-3 ${f.match ? "" : "font-semibold text-amber-700"}`}>{fmtVal(f.uploaded, f.kind)}</td>
                                    <td className={`py-1 pr-3 ${f.match ? "" : "font-semibold text-amber-700"}`}>{fmtVal(f.auto, f.kind)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}

                          {p.components && p.components.rows.length > 0 && (
                            <div>
                              <div className="text-xs font-medium text-gray-500 mb-1">Components</div>
                              <div className="space-y-1">
                                {p.components.rows.map((c, ci) => {
                                  const cm = STATUS_META[c.status] || STATUS_META.mismatch;
                                  return (
                                    <div key={ci} className="border rounded-md" data-testid={`bom-compare-comp-${p.product_code}-${ci}`}>
                                      <div className="flex items-center gap-2 px-2 py-1 bg-white">
                                        <span className={`w-1.5 h-1.5 rounded-full ${cm.dot}`} />
                                        <span className="text-xs flex-1 truncate">{c.description}</span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${cm.cls}`}>{cm.label}</span>
                                      </div>
                                      {c.fields && c.fields.length > 0 && c.status === "mismatch" && (
                                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 px-3 py-1.5 text-[11px] text-gray-600 border-t bg-gray-50/50">
                                          {c.fields.filter((f) => !f.match).map((f, fi) => (
                                            <span key={fi}>
                                              <span className="text-gray-400">{f.label}:</span>{" "}
                                              <span className="text-amber-700 font-medium">{fmtVal(f.uploaded, f.kind)}</span>
                                              {" → "}
                                              <span className="text-amber-700 font-medium">{fmtVal(f.auto, f.kind)}</span>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {data && data.po_id && (
            <div className="flex items-center gap-2 mr-auto w-full sm:w-auto">
              <Input
                value={flagNote}
                onChange={(e) => setFlagNote(e.target.value)}
                placeholder="Optional note for review…"
                className="h-9 text-sm w-64"
                disabled={flagged}
                data-testid="bom-compare-flag-note"
              />
              <Button
                variant="outline"
                onClick={handleFlag}
                disabled={flagged}
                className={flagged ? "text-emerald-700 border-emerald-200" : "text-red-700 border-red-300 hover:bg-red-50"}
                data-testid="bom-compare-flag-btn"
              >
                <Flag className={`w-4 h-4 mr-1.5 ${flagged ? "text-emerald-600 fill-emerald-500" : "text-red-500 fill-red-500"}`} />
                {flagged ? "PO flagged ✓" : "Flag PO for review"}
              </Button>
            </div>
          )}
          <Button variant="outline" onClick={onClose} data-testid="bom-compare-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default POManagement;
